import React, { useCallback } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { StreetViewPhoto } from 'components/shared/StreetViewPhoto';
import { withDashboardOriginSurface } from 'helpers/dashboardSession';
import { useRouter } from 'next/router';

import { HubPropertyPhoto } from './HubPropertyPhoto';
import type { HubSectionId } from './hubSectionOrdering';
import { TRANSACTION_CARD_CLASSES } from './TransactionCardLayout';
import { useHubSectionTracking } from './useHubSectionTracking';

type TransactionCardMedia =
  | { kind: 'image'; url: string | null }
  | { kind: 'street-view'; street: string; city: string; state: string; mapUrl: string | null };

export interface TransactionCardModel {
  id: string;
  href: string;
  media: TransactionCardMedia;
  addressLine: string;
  priceLabel: string | null;
  detailsLine: string | null;
  isMuted: boolean;
  /** A successfully-finished transaction: its progress bar stays accent-colored
   * and fully filled even though the rest of the (past) card is muted. */
  isComplete: boolean;
  totalSegments: number;
  completedSegments: number;
  statusLabel: string;
  footerMessage: string | null;
  /** Omit the CTA fields to render the footer without an action button. */
  ctaLabel?: string | null;
  ctaAnalyticsName?: string;
  ctaVariant?: 'primary' | 'secondary';
}

const isExternalHref = (href: string): boolean => /^https?:\/\//.test(href);

const ProgressSegment: React.FC<{ isFilled: boolean; isAccent: boolean }> = ({
  isFilled,
  isAccent,
}) => (
  <Box
    className={`h-[14px] w-[3px] rounded-[1px] ${isAccent ? 'bg-od-bg-accent-brand' : 'bg-od-border-primary'} ${isFilled ? 'opacity-100' : 'opacity-30'}`}
  />
);

export const TransactionProgress: React.FC<{
  total: number;
  completed: number;
  isMuted: boolean;
  isComplete: boolean;
}> = ({ total, completed, isMuted, isComplete }) => {
  if (total <= 0) {
    return null;
  }
  // A completed transaction keeps its bar accent-colored to read as a success,
  // even though its past card is otherwise muted; dead states (withdrawn,
  // canceled) stay grey.
  const isAccent = isComplete || !isMuted;
  return (
    <Row className="items-center gap-1">
      {Array.from({ length: total }).map((_, index) => (
        <ProgressSegment key={index} isFilled={index < completed} isAccent={isAccent} />
      ))}
    </Row>
  );
};

const renderMediaContent = (media: TransactionCardMedia): React.ReactNode => {
  if (media.kind === 'street-view') {
    return (
      <StreetViewPhoto
        address={{ street: media.street, city: media.city, state: media.state }}
        mapUrl={media.mapUrl}
        // Match each slot's aspect so object-cover does not crop Google's attribution: mobile is
        // 5:2, desktop is the 197x131 slot at 2x. The slot stays 5:2 until `md`.
        size={{
          mobile: { width: 640, height: 256 },
          desktop: { width: 394, height: 262 },
        }}
        desktopMedia="(min-width: 768px)"
      />
    );
  }

  return <HubPropertyPhoto photoUrl={media.url} />;
};

// Canceled/past transactions render their media slightly faded.
const TransactionMedia: React.FC<{ media: TransactionCardMedia; isMuted: boolean }> = ({
  media,
  isMuted,
}) => (
  <Box
    className={`${TRANSACTION_CARD_CLASSES.mediaSlot} bg-od-bg-secondary ${isMuted ? 'opacity-[0.55]' : ''}`}
  >
    {renderMediaContent(media)}
  </Box>
);

interface TransactionCardProps {
  section: HubSectionId;
  transaction: TransactionCardModel;
}

export const TransactionCard: React.FC<TransactionCardProps> = ({ section, transaction }) => {
  const router = useRouter();
  const trackSectionInteraction = useHubSectionTracking(section);

  const handlePress = useCallback(() => {
    trackSectionInteraction();

    const href = getCustomerNavigationHref(withDashboardOriginSurface(transaction.href));

    if (isExternalHref(href) && typeof window !== 'undefined') {
      window.location.assign(href);
      return;
    }
    void router.push(href);
  }, [router, trackSectionInteraction, transaction.href]);

  return (
    <Col className={TRANSACTION_CARD_CLASSES.container}>
      <Box className={TRANSACTION_CARD_CLASSES.innerCard}>
        <TransactionMedia media={transaction.media} isMuted={transaction.isMuted} />
        <Col className={TRANSACTION_CARD_CLASSES.body}>
          <Col className={TRANSACTION_CARD_CLASSES.details}>
            <Text as="span" className="line-clamp-2 text-label-md text-od-content-primary">
              {transaction.addressLine}
            </Text>
            {transaction.priceLabel && (
              <Text as="span" className="text-body-sm text-od-content-secondary">
                {transaction.priceLabel}
              </Text>
            )}
            {transaction.detailsLine && (
              <Text as="span" className="truncate text-body-sm text-od-content-secondary">
                {transaction.detailsLine}
              </Text>
            )}
          </Col>
          <Box className={TRANSACTION_CARD_CLASSES.divider} />
          <Row className={TRANSACTION_CARD_CLASSES.status}>
            <TransactionProgress
              total={transaction.totalSegments}
              completed={transaction.completedSegments}
              isMuted={transaction.isMuted}
              isComplete={transaction.isComplete}
            />
            <Text as="span" className="text-body-sm text-od-content-secondary">
              {transaction.statusLabel}
            </Text>
          </Row>
        </Col>
      </Box>
      <Row
        className={cn(TRANSACTION_CARD_CLASSES.footer, !transaction.footerMessage && 'justify-end')}
      >
        {transaction.footerMessage && (
          <Text as="span" className={TRANSACTION_CARD_CLASSES.footerMessage}>
            {transaction.footerMessage}
          </Text>
        )}
        {transaction.ctaLabel && (
          <Button
            analyticsName={transaction.ctaAnalyticsName ?? ''}
            aria-label={transaction.ctaLabel}
            onClick={handlePress}
            size="md"
            variant={transaction.ctaVariant ?? 'primary'}
            className="text-label-sm"
          >
            {transaction.ctaLabel}
          </Button>
        )}
      </Row>
    </Col>
  );
};
